/**
 * BeneficiosDeDiasSection — Nómina → Configuración → Beneficios de días (#1765).
 *
 * Días libres pagados que la empresa da además de las vacaciones: días
 * administrativos, medio día de cumpleaños, etc. Todo lo que es política de la
 * empresa se configura acá; nada está atado a la realidad de un tenant.
 *
 * - La lista muestra cada beneficio con su política resumida en una línea.
 * - El modal separa lo básico de lo avanzado (fecha de referencia, acumulación),
 *   que va plegado: la mayoría de los beneficios no lo usa.
 * - Las excepciones por ficha (días distintos o excluida) van en su propio modal:
 *   cubren «este cargo tiene 5 días» mientras el cargo sea texto libre.
 * - Un beneficio con solicitudes no se borra: se desactiva (el backend responde
 *   `HAS_REQUESTS`), porque ese historial es el que respalda los saldos.
 */
import { useMemo, useState } from 'react'
import { CalendarHeart, Pencil, Plus, Trash2, Users } from 'lucide-react'

import { Button } from '@/components/ds/Button'
import { Modal } from '@/components/ds/Modal'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { CheckboxField } from '@/components/ds/CheckboxField'
import { ConfirmModal } from '@/components/ConfirmModal'
import { useToast } from '@/components/Toast'
import { extractErrorMessage } from '@/lib/errorUtils'

import { useEmployees } from '../hooks/usePayroll'
import {
  type AsignacionDeBeneficio, type BeneficioDeDias,
  INHABIL_LABEL, PERIODO_LABEL, REFERENCIA_LABEL,
  formatoDias, resumenDelBeneficio,
  useAsignaciones, useBeneficios, useBorrarAsignacion, useBorrarBeneficio,
  useGuardarAsignacion, useGuardarBeneficio,
} from '../hooks/useBeneficios'

type Borrador = Omit<BeneficioDeDias, 'id' | 'asignaciones_count'> & { id?: string }

const NUEVO: Borrador = {
  nombre: '',
  activo: true,
  dias_por_periodo: '1',
  periodo: 'calendario',
  proporcional_al_ingreso: false,
  permite_medio_dia: false,
  acumulable: false,
  tope_acumulado: null,
  aplica_a: 'todos',
  fecha_de_referencia: '',
  ventana_antes: 0,
  ventana_despues: 0,
  si_cae_en_inhabil: 'habil_anterior_o_siguiente',
  requiere_segundo_aprobador: false,
  requiere_motivo: false,
}

/** Errores de campo del backend (`error.details`) como mapa campo → mensaje. */
function erroresDeCampo(e: unknown): Record<string, string> {
  const details = (e as any)?.response?.data?.error?.details
  if (!details || typeof details !== 'object') return {}
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(details)) out[k] = Array.isArray(v) ? String(v[0]) : String(v)
  return out
}

export function BeneficiosDeDiasSection({ canEdit }: { canEdit: boolean }) {
  const { data: beneficios = [], isLoading } = useBeneficios()
  const [editando, setEditando] = useState<Borrador | null>(null)
  const [excepcionesDe, setExcepcionesDe] = useState<BeneficioDeDias | null>(null)
  const [borrando, setBorrando] = useState<BeneficioDeDias | null>(null)
  const borrar = useBorrarBeneficio()
  const guardar = useGuardarBeneficio()
  const toast = useToast()

  const confirmarBorrado = () => {
    if (!borrando) return
    borrar.mutate(borrando.id, {
      onSuccess: () => { toast.success('Beneficio eliminado.'); setBorrando(null) },
      onError: e => {
        toast.error(extractErrorMessage(e, 'No se pudo eliminar.'))
        setBorrando(null)
      },
    })
  }

  const alternarActivo = (b: BeneficioDeDias) => {
    guardar.mutate({ id: b.id, activo: !b.activo }, {
      onSuccess: () => toast.success(b.activo ? 'Beneficio desactivado.' : 'Beneficio activado.'),
      onError: e => toast.error(extractErrorMessage(e, 'No se pudo guardar.')),
    })
  }

  return (
    <section className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h3 className="text-sm font-semibold text-content flex items-center gap-2">
          <CalendarHeart size={15} className="text-content-muted" aria-hidden="true" />
          Beneficios de días
        </h3>
        {canEdit && (
          <Button size="sm" variant="secondary" icon={<Plus size={14} />}
                  onClick={() => setEditando({ ...NUEVO })}>
            Nuevo beneficio
          </Button>
        )}
      </div>
      <p className="text-xs text-content-muted mb-4">
        Días libres pagados además de las vacaciones: días administrativos, medio día de cumpleaños u
        otros. Se piden desde el portal como un permiso, con su propio saldo, y no descuentan días de
        la liquidación.
      </p>

      {isLoading ? (
        <p className="text-sm text-content-subtle">Cargando…</p>
      ) : beneficios.length === 0 ? (
        <p className="text-sm text-content-muted">
          No hay beneficios configurados. Mientras no haya ninguno, el portal no muestra esta sección.
        </p>
      ) : (
        <ul className="divide-y divide-divider">
          {beneficios.map(b => (
            <li key={b.id} className="py-3 first:pt-0 last:pb-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-content flex items-center gap-2">
                    {b.nombre}
                    {!b.activo && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-warning-surface text-warning-content border border-warning-border">
                        Inactivo
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-content-muted mt-0.5">{resumenDelBeneficio(b)}</p>
                  {b.asignaciones_count > 0 && (
                    <p className="text-xs text-content-subtle mt-0.5">
                      {b.asignaciones_count} {b.asignaciones_count === 1 ? 'excepción' : 'excepciones'} por ficha
                    </p>
                  )}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" icon={<Users size={14} />}
                            onClick={() => setExcepcionesDe(b)} aria-label={`Excepciones de ${b.nombre}`}>
                      Excepciones
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Pencil size={14} />}
                            onClick={() => setEditando({ ...b })} aria-label={`Editar ${b.nombre}`} />
                    <Button size="sm" variant="ghost" onClick={() => alternarActivo(b)}>
                      {b.activo ? 'Desactivar' : 'Activar'}
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Trash2 size={14} />}
                            onClick={() => setBorrando(b)} aria-label={`Eliminar ${b.nombre}`} />
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editando && <ModalBeneficio inicial={editando} onCerrar={() => setEditando(null)} />}
      {excepcionesDe && (
        <ModalExcepciones beneficio={excepcionesDe} onCerrar={() => setExcepcionesDe(null)} />
      )}
      <ConfirmModal
        open={!!borrando}
        message={`¿Eliminar «${borrando?.nombre ?? ''}»?`}
        confirmLabel="Eliminar"
        isPending={borrar.isPending}
        onConfirm={confirmarBorrado}
        onCancel={() => setBorrando(null)}
      />
    </section>
  )
}

// ─── Modal del beneficio ────────────────────────────────────────────────────

function ModalBeneficio({ inicial, onCerrar }: { inicial: Borrador; onCerrar: () => void }) {
  const [b, setB] = useState<Borrador>(inicial)
  const [errores, setErrores] = useState<Record<string, string>>({})
  const [avanzado, setAvanzado] = useState(
    !!inicial.fecha_de_referencia || inicial.acumulable || inicial.aplica_a === 'asignados')
  const guardar = useGuardarBeneficio()
  const toast = useToast()
  const set = <K extends keyof Borrador>(k: K, v: Borrador[K]) => setB(prev => ({ ...prev, [k]: v }))

  const enviar = () => {
    const local: Record<string, string> = {}
    if (!b.nombre.trim()) local.nombre = 'Falta el nombre.'
    const dias = parseFloat(String(b.dias_por_periodo).replace(',', '.'))
    if (!isFinite(dias) || dias <= 0 || (dias * 2) % 1 !== 0) {
      local.dias_por_periodo = 'Debe ser mayor que cero y en pasos de medio día.'
    }
    if (Object.keys(local).length) { setErrores(local); return }
    setErrores({})
    const payload: Partial<BeneficioDeDias> = {
      ...b,
      nombre: b.nombre.trim(),
      dias_por_periodo: String(dias),
      tope_acumulado: b.acumulable && b.tope_acumulado
        ? String(parseFloat(String(b.tope_acumulado).replace(',', '.'))) : null,
    }
    guardar.mutate(payload, {
      onSuccess: () => { toast.success('Beneficio guardado.'); onCerrar() },
      onError: e => {
        const campos = erroresDeCampo(e)
        setErrores(Object.keys(campos).length ? campos : { _: extractErrorMessage(e, 'No se pudo guardar.') })
      },
    })
  }

  return (
    <Modal
      open onClose={onCerrar} size="md" dismissOnScrimClick={false}
      title={b.id ? 'Editar beneficio' : 'Nuevo beneficio de días'}
      subtitle={resumenDelBeneficio({ ...b, dias_por_periodo: String(b.dias_por_periodo).replace(',', '.') })}
      actions={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onCerrar}>Cancelar</Button>
          <Button variant="primary" loading={guardar.isPending} onClick={enviar}>Guardar</Button>
        </div>
      }
    >
      <div className="space-y-4">
        <TextField dense label="Nombre" required value={b.nombre}
                   onChange={e => set('nombre', e.target.value)}
                   placeholder="Días administrativos" error={errores.nombre} />
        <div className="grid grid-cols-2 gap-3">
          <TextField dense label="Días por período" required value={b.dias_por_periodo}
                     onChange={e => set('dias_por_periodo', e.target.value)}
                     inputProps={{ inputMode: 'decimal' }}
                     hint="En pasos de medio día (0,5)." error={errores.dias_por_periodo} />
          <SelectField dense label="Período" value={b.periodo}
                       onChange={e => {
                         const periodo = e.target.value as Borrador['periodo']
                         setB(prev => ({
                           ...prev, periodo,
                           // Desde el ingreso nadie entra a mitad de período.
                           proporcional_al_ingreso: periodo === 'aniversario' ? false : prev.proporcional_al_ingreso,
                         }))
                       }}
                       options={Object.entries(PERIODO_LABEL).map(([value, label]) => ({ value, label }))} />
        </div>
        <div className="space-y-2">
          <CheckboxField label="Permitir medios días"
                         description="Se puede pedir media jornada (mañana o tarde)."
                         checked={b.permite_medio_dia}
                         onChange={e => set('permite_medio_dia', e.target.checked)} />
          {errores.permite_medio_dia && <p className="text-xs text-danger-solid">{errores.permite_medio_dia}</p>}
          <CheckboxField label="Proporcional al ingreso"
                         description="Quien ingresa a mitad del año recibe la parte proporcional, redondeada al medio día hacia abajo."
                         checked={b.proporcional_al_ingreso}
                         disabled={b.periodo === 'aniversario'}
                         onChange={e => set('proporcional_al_ingreso', e.target.checked)} />
          <CheckboxField label="Requiere segundo aprobador"
                         description="Después de la jefatura, aprueba la persona configurada como segundo aprobador."
                         checked={b.requiere_segundo_aprobador}
                         onChange={e => set('requiere_segundo_aprobador', e.target.checked)} />
          <CheckboxField label="Motivo obligatorio"
                         checked={b.requiere_motivo}
                         onChange={e => set('requiere_motivo', e.target.checked)} />
          <CheckboxField label="Activo"
                         description="Inactivo no se puede pedir; el historial se conserva."
                         checked={b.activo}
                         onChange={e => set('activo', e.target.checked)} />
        </div>

        <button type="button" onClick={() => setAvanzado(v => !v)}
                className="text-xs font-medium text-primary hover:underline" aria-expanded={avanzado}>
          {avanzado ? 'Ocultar opciones avanzadas' : 'Opciones avanzadas: fecha de referencia, acumulación, a quién aplica'}
        </button>

        {avanzado && (
          <div className="space-y-4 border-t border-divider pt-4">
            <SelectField dense label="Fecha de referencia" value={b.fecha_de_referencia}
                         onChange={e => set('fecha_de_referencia', e.target.value as Borrador['fecha_de_referencia'])}
                         options={Object.entries(REFERENCIA_LABEL).map(([value, label]) => ({ value, label }))}
                         hint="Con una fecha, el beneficio se toma en torno a ella (p. ej. el cumpleaños)." />
            {b.fecha_de_referencia && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <TextField dense label="Días hábiles antes" value={b.ventana_antes}
                             onChange={e => set('ventana_antes', Math.max(0, parseInt(e.target.value, 10) || 0))}
                             inputProps={{ type: 'number', min: 0 }} error={errores.ventana_antes} />
                  <TextField dense label="Días hábiles después" value={b.ventana_despues}
                             onChange={e => set('ventana_despues', Math.max(0, parseInt(e.target.value, 10) || 0))}
                             inputProps={{ type: 'number', min: 0 }} error={errores.ventana_despues} />
                </div>
                <p className="text-xs text-content-subtle -mt-2">0 y 0 = solo ese día.</p>
                <SelectField dense label="Si la fecha cae en fin de semana o feriado"
                             value={b.si_cae_en_inhabil}
                             onChange={e => set('si_cae_en_inhabil', e.target.value as Borrador['si_cae_en_inhabil'])}
                             options={Object.entries(INHABIL_LABEL).map(([value, label]) => ({ value, label }))} />
              </>
            )}
            <CheckboxField label="Acumulable"
                           description="Lo no usado pasa al período siguiente."
                           checked={b.acumulable}
                           onChange={e => set('acumulable', e.target.checked)} />
            {b.acumulable && (
              <TextField dense label="Tope acumulado (opcional)" value={b.tope_acumulado ?? ''}
                         onChange={e => set('tope_acumulado', e.target.value || null)}
                         inputProps={{ inputMode: 'decimal' }}
                         hint="Máximo de días disponibles sumando el arrastre. Vacío = sin tope."
                         error={errores.tope_acumulado} />
            )}
            <SelectField dense label="Aplica a" value={b.aplica_a}
                         onChange={e => set('aplica_a', e.target.value as Borrador['aplica_a'])}
                         options={[
                           { value: 'todos', label: 'Todas las fichas activas' },
                           { value: 'asignados', label: 'Solo las fichas con una excepción que lo asigne' },
                         ]}
                         hint="Las excepciones por ficha (días distintos o excluida) se editan desde «Excepciones»." />
          </div>
        )}
        {errores._ && <p role="alert" className="text-sm text-danger-solid">{errores._}</p>}
      </div>
    </Modal>
  )
}

// ─── Modal de excepciones por ficha ─────────────────────────────────────────

function ModalExcepciones({ beneficio, onCerrar }: { beneficio: BeneficioDeDias; onCerrar: () => void }) {
  const { data: asignaciones = [], isLoading } = useAsignaciones(beneficio.id)
  const { data: empData } = useEmployees({ active: 'true', page_size: 9999 })
  const guardar = useGuardarAsignacion(beneficio.id)
  const borrar = useBorrarAsignacion(beneficio.id)
  const toast = useToast()

  const [ficha, setFicha] = useState('')
  const [modo, setModo] = useState<'dias' | 'excluido'>('dias')
  const [dias, setDias] = useState('')
  const [nota, setNota] = useState('')
  const [error, setError] = useState<string | null>(null)

  const conExcepcion = useMemo(() => new Set(asignaciones.map(a => a.employee)), [asignaciones])
  const opciones = ((empData?.employees || []) as { id: string; full_name: string }[])
    .filter(f => !conExcepcion.has(f.id))
    .map(f => ({ value: f.id, label: f.full_name }))

  const agregar = () => {
    if (!ficha) { setError('Falta elegir la ficha.'); return }
    let diasNum: number | null = null
    // Sin días escritos = asignada con los días del beneficio (sirve con «Solo las
    // fichas asignadas»).
    if (modo === 'dias' && dias.trim()) {
      diasNum = parseFloat(dias.replace(',', '.'))
      if (!isFinite(diasNum) || diasNum < 0 || (diasNum * 2) % 1 !== 0) {
        setError('Los días deben ser cero o más, en pasos de medio día.'); return
      }
    }
    setError(null)
    guardar.mutate({
      employee: ficha,
      excluido: modo === 'excluido',
      dias_por_periodo: diasNum == null ? null : String(diasNum),
      nota: nota.trim(),
    }, {
      onSuccess: () => { setFicha(''); setDias(''); setNota(''); toast.success('Excepción agregada.') },
      onError: e => setError(extractErrorMessage(e, 'No se pudo guardar.')),
    })
  }

  const describir = (a: AsignacionDeBeneficio) =>
    a.excluido ? 'Excluida: no tiene este beneficio'
      : a.dias_por_periodo != null ? `${formatoDias(a.dias_por_periodo)} días por período`
        : `Asignada con los días del beneficio (${formatoDias(beneficio.dias_por_periodo)})`

  return (
    <Modal open onClose={onCerrar} size="lg" title={`Excepciones — ${beneficio.nombre}`}
           subtitle={beneficio.aplica_a === 'asignados'
             ? 'Solo las fichas de esta lista tienen el beneficio (salvo las excluidas).'
             : `Todas las fichas activas tienen ${formatoDias(beneficio.dias_por_periodo)} días; acá van las que tienen otra cantidad o no lo tienen.`}
           actions={<div className="flex justify-end"><Button variant="secondary" onClick={onCerrar}>Cerrar</Button></div>}>
      <div className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-content-subtle">Cargando…</p>
        ) : asignaciones.length === 0 ? (
          <p className="text-sm text-content-muted">Sin excepciones.</p>
        ) : (
          <ul className="divide-y divide-divider border border-divider rounded-lg">
            {asignaciones.map(a => (
              <li key={a.id} className="flex items-start justify-between gap-3 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm text-content">{a.employee_name}
                    {a.employee_position && <span className="text-content-subtle"> · {a.employee_position}</span>}
                  </p>
                  <p className="text-xs text-content-muted">{describir(a)}{a.nota ? ` — ${a.nota}` : ''}</p>
                </div>
                <Button size="sm" variant="ghost" icon={<Trash2 size={14} />}
                        aria-label={`Quitar la excepción de ${a.employee_name}`}
                        loading={borrar.isPending && borrar.variables === a.id}
                        onClick={() => borrar.mutate(a.id, {
                          onError: e => toast.error(extractErrorMessage(e, 'No se pudo quitar.')),
                        })} />
              </li>
            ))}
          </ul>
        )}

        <div className="border-t border-divider pt-4 space-y-3">
          <p className="text-xs font-semibold text-content">Agregar una excepción</p>
          <SelectField dense label="Ficha" value={ficha} onChange={e => setFicha(e.target.value)}
                       placeholder="Elegir una ficha" options={opciones} />
          <div className="grid grid-cols-2 gap-3">
            <SelectField dense label="Tipo" value={modo}
                         onChange={e => setModo(e.target.value as 'dias' | 'excluido')}
                         options={[
                           { value: 'dias', label: beneficio.aplica_a === 'asignados' ? 'Asignada' : 'Otra cantidad de días' },
                           { value: 'excluido', label: 'Excluida (sin el beneficio)' },
                         ]} />
            {modo === 'dias' && (
              <TextField dense label="Días por período" value={dias}
                         onChange={e => setDias(e.target.value)}
                         inputProps={{ inputMode: 'decimal' }}
                         placeholder={formatoDias(beneficio.dias_por_periodo)}
                         hint="Vacío = los días del beneficio." />
            )}
          </div>
          <TextField dense label="Nota (opcional)" value={nota} onChange={e => setNota(e.target.value)}
                     placeholder="Por cargo, por contrato…" />
          {error && <p role="alert" className="text-sm text-danger-solid">{error}</p>}
          <div className="flex justify-end">
            <Button size="sm" variant="primary" icon={<Plus size={14} />}
                    loading={guardar.isPending} onClick={agregar}>
              Agregar
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
